import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Typography, IconButton, Box, Avatar } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Spinner from '../../common/Spinner';
import { ROUTES } from '../../../constants/routes';
import { terminalsService } from '../../../services/terminalsService';
import { TerminalDetailsDto } from '../../../models/terminals/terminalDetailsDto';
import { EditTerminalModal } from '../edit/EditTerminalModal';
import TmPencil from '../../../icons/TmPencil';
import { CategoryType } from '../../../models/enums/categoryType';
import { PermissionType } from '../../../models/enums/permissionType';
import { ShowForPermission } from '../../common/permissions/ShowForPermission';
import ErrorAlert from '../../common/ErrorAlert';
import { CaseIntegrityCard } from './CaseIntegrityCard';
import { CaseIntegrityHistoryCard } from './CaseIntegrityHistoryCard';
import { ClientsCard } from './ClientsCard';
import { GeneralInfoCard } from './GeneralInfoCard';
import { NiiatInfoCard } from './NiiatInfoCard';
import { SoftwareHashesCard } from './SoftwareHashesCard';


export const TerminalDetails: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [terminal, setTerminal] = useState<TerminalDetailsDto>();
  const [isEditPopupOpen, setEditPopupOpen] = useState(false);
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    const terminalId = Number(params.id);

    terminalsService.getById(terminalId)
      .then(r => setTerminal(r.data.data))
      .catch(e => {
        console.error(e);
        if (e.response.status === 404) {
          navigate(ROUTES.auth.notFound);
        }
      })
      .finally(() => setLoading(false));
  }, [params.id, navigate]);

  const refreshData = () => {
    if (!params.id) return;

    const terminalId = Number(params.id);
    setLoading(true);

    terminalsService.getById(terminalId)
      .then(r => setTerminal(r.data.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  function handleEditPopupToggle(): void {
    setEditPopupOpen(!isEditPopupOpen);
  }

  const handleIntegrityCheckChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!terminal?.id) return;

    try {
      setLoading(true);
      await terminalsService.updateIntegrityCheck(terminal.id, !event.target.checked);
      refreshData();
    } catch (e: any) {
      console.log(e);
      const error = e.response.data.message ?? e.message;
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetCaseIntegrityCheck = async () => {
    if (!terminal?.id) return;

    try {
      setLoading(true);
      await terminalsService.resetCaseIntegrityCheck(terminal.id);
      refreshData();
    } catch (e: any) {
      console.log(e);
      const error = e.response.data.message ?? e.message;
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRepeatCheckupsChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!terminal?.id) return;

    try {
      setLoading(true);
      await terminalsService.updateVerifyRepeatCheckups(terminal.id, event.target.checked);
      refreshData();
    } catch (e: any) {
      console.log(e);
      const error = e.response.data.message ?? e.message;
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {loading
        ? <Spinner />
        : (<Box>

          {error && (
            <ErrorAlert
              message={error}
              onClose={() => setError('')}
              autoCloseDuration={10000}
            />
          )}

          <Grid container alignItems="center" justifyContent="space-between">

            <Grid item xs={2}>
              <IconButton onClick={() => navigate(-1)} sx={{ my: 2 }}>
                <Avatar sx={{ backgroundColor: "white", border: 1, borderColor: "#9588e8" }}>
                  <ChevronLeftIcon sx={{ color: '#9588e8' }} />
                </Avatar>
              </IconButton>
            </Grid>

            <Grid item xs={8} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="h6" align="center">Данные терминала</Typography>
            </Grid>

            <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ShowForPermission catalog={CategoryType.Terminals} permission={PermissionType.Edit}>
                <IconButton className="list-item" sx={{ px: "20px" }} onClick={handleEditPopupToggle}>
                  <TmPencil fill="none" stroke="white" />&nbsp;&nbsp;
                  <Typography fontWeight="bold">Редактировать</Typography>
                </IconButton>
              </ShowForPermission>
            </Grid>

          </Grid>

          <Grid container spacing={2} rowSpacing={2} columnSpacing={2}>
            {/* Row 1 */}
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <GeneralInfoCard
                terminal={terminal}
                onIntegrityCheckChange={handleIntegrityCheckChange}
                onVerifyRepeatCheckupsChange={handleVerifyRepeatCheckupsChange}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
              <ClientsCard terminal={terminal} />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', mt: 2 }}>
              <NiiatInfoCard terminal={terminal} />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', mt: 2 }}>
              <CaseIntegrityCard
                terminal={terminal}
                onResetCaseIntegrityCheck={handleResetCaseIntegrityCheck}
              />
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', mt: 2 }}>
              <SoftwareHashesCard terminal={terminal} />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', mt: 2 }}>
              <CaseIntegrityHistoryCard terminal={terminal} />
            </Grid>
          </Grid>

          <EditTerminalModal onClose={handleEditPopupToggle} onRefresh={refreshData} isAdding={false} terminal={terminal} open={isEditPopupOpen} />
        </Box>)}
    </Box >
  );
};
